/**
 * Texas Custom Neon - Neon Text Generator
 * 
 * Simplified implementation focused on displaying neon text with HTML/CSS.
 * No canvas or SVG elements are used.
 */

// ==========================================================================
// CONSTANTS AND CONFIGURATION
// ==========================================================================
const fontFamilies = [
  {
    name: "Barcelona",
    family: "Barcelona",
  },
  {
    name: "Alexa",
    family: "Alexa",
  },
  {
    name: "Bayview",
    family: "Bayview",
  },
  {
    name: "Amsterdam",
    family: "Amsterdam",
  },
  {
    name: "Greenworld",
    family: "Greenworld",
  },
  {
    name: "NewCursive",
    family: "NewCursive",
  },
  {
    name: "Vintage",
    family: "Vintage",
  },
  {
    name: "Venetian",
    family: "Venetian",
  },
  {
    name: "Amanda",
    family: "Amanda",
  },
  {
    name: "Austin",
    family: "Austin",
  },
  {
    name: "Beachfront",
    family: "Beachfront",
  },
  {
    name: "Chelsea",
    family: "Chelsea",
  },
  {
    name: "Freehand",
    family: "Freehand",
  },
  {
    name: "Freespirit",
    family: "Freespirit",
  },
  {
    name: "LoveNote",
    family: "LoveNote",
  },
  {
    name: "Neonscript",
    family: "Neonscript",
  },
  {
    name: "Northshore",
    family: "Northshore",
  },
  {
    name: "Photogenic",
    family: "Photogenic",
  },
  {
    name: "Royalty",
    family: "Royalty",
  },
  {
    name: "Rocket",
    family: "Rocket",
  },
  {
    name: "Signature",
    family: "Signature",
  },
  {
    name: "Sorrento",
    family: "Sorrento",
  },
  {
    name: "WildScript",
    family: "WildScript",
  },
  {
    name: "Avante",
    family: "Avante",
  },
  {
    name: "Buttercup",
    family: "Buttercup",
  },
  {
    name: "ClassicType",
    family: "ClassicType",
  },
  {
    name: "Typewriter",
    family: "Typewriter",
  },
  {
    name: "Melbourne",
    family: "Melbourne",
  },
  {
    name: "NeoTokyo",
    family: "NeoTokyo",
  },
  {
    name: "Monaco",
    family: "Monaco",
  },
  {
    name: "Waikiki",
    family: "Waikiki",
  },
  {
    name: "Bellview",
    family: "Bellview",
  },
  {
    name: "LoveNeon",
    family: "LoveNeon",
  },
  {
    name: "Marquee",
    family: "Marquee",
  },
  {
    name: "Mayfair",
    family: "Mayfair",
  },
  {
    name: "NeonGlow",
    family: "NeonGlow",
  },
  {
    name: "NeonLite",
    family: "NeonLite",
  },
  {
    name: "Neontrace",
    family: "Neontrace",
  },
  {
    name: "Nevada",
    family: "Nevada",
  },
  {
    name: "SciFi",
    family: "SciFi",
  },
];

// ==========================================================================
// STATE MANAGEMENT
// ==========================================================================

/**
 * Global state object that holds the current neon sign configuration.
 * This object is updated as the user interacts with the UI and is used
 * for calculations, rendering, and checkout.
 */
const neonObject = {
  text: "Your Text",
  fontFamily: "Avante",
  fontSize: "60px",
  color: "#F6E7D2", // Default color (white)
  plan: {},
  lineHeight: 20,
  variantId: "gid://shopify/ProductVariant/47595020091680",
  discountApplied: false,
  widthInches: 0,
  heightInches: 0
};

// Constants
const BASE_PRICE = 90; // Base price in dollars per square inch
let basePrice = BASE_PRICE; // For backward compatibility
let textArea;

// Initialize text area reference when DOM is loaded
document.addEventListener("DOMContentLoaded", function() {
  textArea = document.getElementById("text-panel");
  if (textArea && textArea.value) {
    neonObject.text = textArea.value.trim() || "Your Text";
  }
  console.log("Text area initialized:", textArea);
});

// ==========================================================================
// INITIALIZATION FUNCTIONS
// ==========================================================================

/**
 * Initialize the font palette with improved DOM handling but keeping original structure
 * This implementation improves DOM stability while maintaining the familiar layout
 */
function initFontPallate() {
  console.log('Initializing font palette with original structure');
  const fontPallate = document.getElementById("font-pallate");
  if (!fontPallate) {
    console.error("Font palette element not found");
    return;
  }
  
  // Clear existing content
  fontPallate.innerHTML = '';
  
  console.log('Creating font options, found palette:', fontPallate);
  
  // Pre-defined sizes for various fonts
  const fontSizes = {
    "Barcelona": "24px",
    "Alexa": "22px",
    "Bayview": "25px",
    "Amsterdam": "27px",
    "Greenworld": "28px",
    "NewCursive": "28px",
    "Vintage": "27px",
    "Venetian": "27px",
    "Amanda": "27px",
    "Austin": "46px",
    "Beachfront": "22px",
    "Chelsea": "36px",
    "Freehand": "27px",
    "Freespirit": "19px",
    "LoveNote": "27px",
    "Neonscript": "27px",
    "Northshore": "27px",
    "Photogenic": "27px",
    "Royalty": "22px",
    "Rocket": "23px",
    "Signature": "27px",
    "Sorrento": "27px",
    "WildScript": "27px",
    "Avante": "27px",
    "Buttercup": "27px",
    "ClassicType": "18px",
    "Typewriter": "20px",
    "Melbourne": "22px",
    "NeoTokyo": "22px",
    "Monaco": "27px",
    "Waikiki": "27px",
    "Bellview": "39px",
    "LoveNeon": "19px",
    "Marquee": "27px",
    "Mayfair": "27px",
    "NeonGlow": "24px",
    "NeonLite": "23px",
    "Neontrace": "18px",
    "Nevada": "35px",
    "SciFi": "27px"
  };

  // Create a heading to know that font palette is working (keeping original structure)
  const heading = document.createElement('h4');
  heading.style.color = 'white';
  heading.style.margin = '10px 0';
  fontPallate.appendChild(heading);

  fontFamilies.forEach((font) => {
    const size = fontSizes[font.name] || "60px";
    
    // Create elements with same structure as before
    const label = document.createElement("label");
    label.className = "custom-radio";
    
    // Create radio input directly
    const input = document.createElement("input");
    input.type = "radio";
    input.name = "color-radio";
    input.value = font.family;
    input.setAttribute("data-name", font.name);
    
    // Create outer wrapper - maintaining original structure
    const span = document.createElement("span");
    span.className = "radio-btn";
    
    // Icon element - same as original
    const icon = document.createElement("i");
    icon.className = "las la-check";
    
    // Font container with direct styling
    const div = document.createElement("div");
    div.className = "hobbies-icon";
    
    // Font name element with improved styling
    const h3 = document.createElement("h3");
    h3.className = "radio-font";
    h3.style.fontFamily = `"${font.family}", sans-serif`; // Properly quoted font family
    h3.style.fontSize = size;
    h3.textContent = font.name;

    // Assemble components in the same structure as before
    div.appendChild(h3);
    span.appendChild(icon);
    span.appendChild(div);
    label.appendChild(input);
    label.appendChild(span);
    fontPallate.appendChild(label);
    
    // Add optimized click handler directly to the label
    label.addEventListener('click', function() {
      // Update font selection immediately
      neonObject.fontFamily = font.family;
      console.log('Font selected:', font.family);
      updateNeonText();
    });
  });
  
  console.log('Font palette initialized with original structure -', fontFamilies.length, 'fonts');
}

// initFontSelection: Sets the initially selected font and triggers first drawing.
function initFontSelection() {
  const initialFontRadio =
    document.querySelector('input[name="color-radio"]:checked') ||
    document.querySelector('input[name="color-radio"]');
  if (initialFontRadio) {
    initialFontRadio.checked = true;
    neonObject.fontFamily = initialFontRadio.value;
    console.log('Set initial font to:', neonObject.fontFamily);
  }
}

/**
 * Initialize color selection with direct DOM implementation for improved stability
 * This function adds optimized event handlers to color selectors without using inner elements
 * and prevents scroll jumping when color options are clicked
 */
function initColorSelector() {
  console.log('Initializing color selector with direct DOM implementation');
  
  // Get all color selector radio inputs
  const colorInputs = document.querySelectorAll('input[name="color-selector"]');
  if (colorInputs.length === 0) {
    console.warn('No color selector elements found');
    return;
  }
  
  console.log('Found', colorInputs.length, 'color options');
  
  // Simple solution to prevent scroll jumping: preserve the scroll position
  let savedPosition = 0;
  
  // Function to stop automatic scrolling
  function preventScrollJump() {
    savedPosition = window.scrollY;
    
    // Force the page to stay at the same position
    setTimeout(() => {
      window.scrollTo(0, savedPosition);
    }, 0);
    
    // And again with a slightly longer delay to catch delayed DOM updates
    setTimeout(() => {
      window.scrollTo(0, savedPosition);
    }, 50);
  }
  
  // For each color input, create a completely fresh implementation
  colorInputs.forEach(input => {
    // Create a completely new input element 
    const newInput = input.cloneNode(true);
    newInput.setAttribute('data-anti-scroll', 'true');
    input.parentNode.replaceChild(newInput, input);
    
    // Get the label associated with this input
    const label = document.querySelector(`label[for="${newInput.id}"]`);
    if (label) {
      // First completely prevent default behavior
      label.addEventListener('click', function(e) {
        // Save the scroll position
        savedPosition = window.scrollY;
        
        // Prevent default browser behavior
        e.preventDefault();
        e.stopPropagation();
        
        // Do everything manually to avoid browser's automatic behaviors
        newInput.checked = true;
        neonObject.color = newInput.value;
        
        // Update the UI
        document.querySelectorAll('.selector-item_label').forEach(l => {
          l.classList.remove('active-color');
        });
        label.classList.add('active-color');
        
        // Update neon text with the new color
        updateNeonText();
        
        // Force the scroll position to stay fixed
        preventScrollJump();
      });
    }
    
    // Handle the input's change event directly
    newInput.addEventListener('change', function(e) {
      // Save scroll position immediately
      savedPosition = window.scrollY;
      
      if (this.checked) {
        // Update color and UI
        neonObject.color = this.value;
        
        // Update UI classes
        document.querySelectorAll('.selector-item_label').forEach(l => {
          l.classList.remove('active-color');
        });
        
        const thisLabel = document.querySelector(`label[for="${this.id}"]`);
        if (thisLabel) {
          thisLabel.classList.add('active-color');
        }
        
        // Update the actual neon text
        updateNeonText();
        
        // Prevent scroll jumping
        preventScrollJump();
      }
    });
  });
  
  // Set initial color from the checked input without causing scroll
  const checkedColor = document.querySelector('input[name="color-selector"]:checked');
  if (checkedColor) {
    neonObject.color = checkedColor.value;
    console.log('Initial color set to:', neonObject.color);
    
    // Apply active class to the initially selected color's label
    const initialLabel = document.querySelector(`label[for="${checkedColor.id}"]`);
    if (initialLabel) {
      initialLabel.classList.add('active-color');
    }
  }
  
  // Apply a global fix to all .selector-item elements (parents of the labels)
  document.querySelectorAll('.selector-item').forEach(item => {
    item.addEventListener('click', function(e) {
      // Save scroll position
      savedPosition = window.scrollY;
      
      // Let the event continue to our custom handlers on the labels
      // but make sure we restore scroll position afterward
      setTimeout(() => {
        window.scrollTo(0, savedPosition);
      }, 10);
    });
  });
  
  // Also handle the parent container to prevent any scroll issues
  const container = document.querySelector('.choose__color___wrap');
  if (container) {
    container.addEventListener('click', function(e) {
      // Save position
      savedPosition = window.scrollY;
      
      // Restore after slight delay
      setTimeout(() => {
        window.scrollTo(0, savedPosition);
      }, 10);
    });
  }
}

// Add a function to update the neon text display, add measurement lines, and calculate pricing
function updateNeonText() {
  console.log('Updating neon text display');
  
  // Find the container
  const container = document.getElementById('mySVG');
  if (!container) {
    console.error('Container not found during update');
    return;
  }
  
  // Clear the container
  container.innerHTML = '';
  
  // Style the container with clean styling
  container.style.backgroundColor = 'transparent';
  container.style.minHeight = '200px';
  container.style.display = 'flex';
  container.style.justifyContent = 'center';
  container.style.alignItems = 'center';
  container.style.position = 'relative';
  
  // Create a wrapper for the text elements with significant padding to prevent overlap
  const textWrapper = document.createElement('div');
  textWrapper.className = 'neon-text-wrapper';
  textWrapper.style.position = 'relative';
  textWrapper.style.padding = '0px'; // Remove padding to control text position more precisely
  textWrapper.style.marginRight = '50px'; // More margin to ensure vertical line doesn't overlap
  textWrapper.style.marginBottom = '50px'; // More margin to ensure horizontal line doesn't overlap
  // Use block display for more predictable measurements
  textWrapper.style.display = 'block';
  container.appendChild(textWrapper);
  
  // Split text into lines
  const lines = (neonObject.text || 'Your Text').split('\n');
  let maxWidth = 0;
  let totalHeight = 0;
  const lineElements = [];
  
  // Create a container for text content to ensure consistent measurements
  const textContainer = document.createElement('div');
  textContainer.style.position = 'relative';
  textContainer.style.display = 'inline-block'; // Size to content
  textContainer.id = 'text-container';
  textWrapper.appendChild(textContainer);
  
  // Create neon text elements for each line with sentinel elements to mark exact boundaries
  lines.forEach((line, index) => {
    // Create a wrapper for each line to precisely contain the text
    const lineWrapper = document.createElement('div');
    lineWrapper.style.display = 'block';
    lineWrapper.style.position = 'relative';
    // Reduce line spacing to just 2px for very tight formatting
    lineWrapper.style.marginBottom = index < lines.length - 1 ? '2px' : '0';
    
    // Create the actual text element
    const neonElement = document.createElement('div');
    neonElement.textContent = line || ' '; // Use space for empty lines
    
    // Add explicit inline styles to ensure text is visible
    neonElement.style.fontFamily = `"${neonObject.fontFamily}", sans-serif`;
    neonElement.style.fontSize = neonObject.fontSize;
    // Minimize line height for extremely tight text spacing
    neonElement.style.lineHeight = '1';
    neonElement.style.whiteSpace = 'nowrap';
    neonElement.style.color = 'white';
    neonElement.style.display = 'block';
    neonElement.style.textAlign = 'center';
    neonElement.style.margin = '0'; // No margin on the text element itself
    neonElement.className = 'neon-text-line';
    neonElement.id = `neon-line-${index}`; // .Add unique ID for easier direct selection
    
    // Apply neon glow effect with color
    neonElement.style.textShadow = 
      `${neonObject.color} 0px 0px 5px, ` +
      `${neonObject.color} 0px 0px 10px, ` +
      `${neonObject.color} 0px 0px 20px, ` +
      `${neonObject.color} 0px 0px 30px, ` +
      `${neonObject.color} 0px 0px 40px`;
    
    // Add the text element to the line wrapper
    lineWrapper.appendChild(neonElement);
    
    // Add the line wrapper to the container
    textContainer.appendChild(lineWrapper);
    
    // Track the text element for measurements
    lineElements.push(neonElement);
  });
  
  // Give time for the browser to render the elements before measuring
  setTimeout(() => {
    // Measure the text elements
    lineElements.forEach(element => {
      maxWidth = Math.max(maxWidth, element.offsetWidth);
      totalHeight += element.offsetHeight;
    });
    
    // Add measurement lines with a slight delay to ensure text is fully rendered
    setTimeout(() => {
      addMeasurementLines(textWrapper, maxWidth, totalHeight);
    }, 10);
    
    // Calculate dimensions using 2 inches per character logic
    // First, get the actual pixel measurements for aspect ratio
    const pixelWidth = maxWidth;
    const pixelHeight = totalHeight;
    
    // Calculate the total character count across all lines
    // For multiline text, we use the longest line's character count
    let maxCharCount = 0;
    lines.forEach(line => {
      maxCharCount = Math.max(maxCharCount, line.length);
    });
    
    // Calculate dimensions: width = 2 inches * character count
    const widthInches = 2 * maxCharCount;
    
    // Calculate height based on width and aspect ratio
    const aspectRatio = pixelWidth / pixelHeight;
    const heightInches = widthInches / aspectRatio;
    
    console.log('New dimension calculation:', 
               'Aspect ratio:', aspectRatio,
               'Width:', widthInches.toFixed(2), 'inches',
               'Height:', heightInches.toFixed(2), 'inches');
    
    // Store the dimensions in the neon object
    neonObject.widthInches = widthInches;
    neonObject.heightInches = heightInches;
    
    calculateAndUpdatePrice(widthInches, heightInches);
    
    console.log('Updated neon text with measurements:', 
                'dimensions:', widthInches.toFixed(2), 'x', heightInches.toFixed(2), 'inches');
  }, 10);
}

/**
 * Load fonts using an improved approach with direct DOM rendering
 * This implementation preloads fonts without nested elements for better page stability
 */
function loadFonts() {
  console.log('Loading fonts with optimized DOM approach');
  
  // Check if fonts are already loaded to avoid duplicate loading
  if (document.getElementById('font-preloader')) {
    console.log('Font preloader already exists');
    return;
  }

  // Create a preloader element with direct styling
  const preloader = document.createElement('div');
  preloader.id = 'font-preloader';
  preloader.style.position = 'absolute';
  preloader.style.visibility = 'hidden';
  preloader.style.opacity = '0';
  preloader.style.width = '0';
  preloader.style.height = '0';
  preloader.style.overflow = 'hidden';
  preloader.style.pointerEvents = 'none';
  preloader.style.zIndex = '-1';
  document.body.appendChild(preloader);
  
  // Create style element for font-face declarations
  const styleEl = document.createElement('style');
  styleEl.id = 'neon-fonts-style';
  
  // Build font-face declarations for all fonts
  let fontFaceRules = `
    /* Ensure the neon text container is properly styled */
    .neon-text-container {
      background-color: transparent;
      min-height: 200px;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
    }
  `;
  
  // Add each font directly to the preloader
  fontFamilies.forEach(font => {
    // Create a simple element for each font with direct styling
    const fontElement = document.createElement('div');
    fontElement.textContent = font.name;
    fontElement.style.fontFamily = `"${font.family}", sans-serif`;
    fontElement.style.fontSize = '16px';
    preloader.appendChild(fontElement);
    
    // Add font-face declaration to style element
    fontFaceRules += `
    @font-face {
      font-family: '${font.family}';
      src: url('/assets/${font.family}.ttf') format('truetype');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
    `;
  });
  
  // Apply all font-face rules at once
  styleEl.textContent = fontFaceRules;
  document.head.appendChild(styleEl);

  console.log('Font preloader created with', fontFamilies.length, 'font elements');

  // Keep the preloader in the DOM but make it completely hidden after fonts are loaded
  // This is more efficient than removing it
  setTimeout(() => {
    preloader.style.display = 'none';
    console.log('Fonts loaded and ready to use');
  }, 3000);
}

// Initialize the app when the DOM is fully loaded
document.addEventListener("DOMContentLoaded", function() {
  console.log('DOM loaded - initializing neon text generator');
  
  // Load fonts first
  loadFonts();
  
  // Initialize font palette with a small delay to ensure DOM is ready
  setTimeout(() => {
    initFontPallate();
    
    // Set up text area for multiline display
    textArea = document.getElementById("text-panel");
    if (textArea) {
      console.log('Found text area, setting up for multiline input');
      
      // Set the textarea to allow multiline input
      textArea.style.whiteSpace = 'pre';
      textArea.style.overflowWrap = 'normal';
      textArea.rows = 3; // Allow multiple rows
      
      // Handle both input and keydown events
      textArea.addEventListener('input', function() {
        neonObject.text = textArea.value || 'Your Text';
        updateNeonText();
      });
      
      // Initialize with text from text area if available
      if (textArea.value) {
        neonObject.text = textArea.value;
      }
    }
    
    // Initialize color selection with direct DOM implementation
    initColorSelector();
    
    // Display initial text
    updateNeonText();
    
    // Force a redraw after a short delay to ensure fonts are loaded
    setTimeout(updateNeonText, 500);
  }, 100);
});

// Also initialize on window load to ensure all assets are loaded
window.addEventListener('load', function() {
  console.log('Window fully loaded - reinitializing neon text');
  updateNeonText();
});

/**
 * Adds measurement lines at the bottom and right side of the text with dimensions.
 * This implementation uses direct DOM measurements to ensure accurate alignment.
 * 
 * @param {HTMLElement} wrapper - The wrapper element containing the text
 * @param {number} width - The width of the text in pixels
 * @param {number} height - The height of the text in pixels
 */
function addMeasurementLines(wrapper, width, height) {
  // First clear any existing measurement lines
  const existingMeasurements = wrapper.querySelector('.measurement-lines');
  if (existingMeasurements) {
    existingMeasurements.remove();
  }
  
  // Create a completely separate container for the measurement lines
  const measurementContainer = document.createElement('div');
  measurementContainer.className = 'measurement-lines';
  measurementContainer.style.position = 'absolute';
  measurementContainer.style.top = '0';
  measurementContainer.style.left = '0';
  measurementContainer.style.width = '100%';
  measurementContainer.style.height = '100%';
  measurementContainer.style.pointerEvents = 'none';
  wrapper.appendChild(measurementContainer);
  
  // Get the text elements to measure
  const textElements = wrapper.querySelectorAll('.neon-text-line');
  
  // Default values if no text is present
  let textRect = {
    top: 0,
    left: 0,
    right: width,
    bottom: height
  };
  
  if (textElements.length > 0) {
    // First get the text container measurements
    const container = wrapper.querySelector('#text-container');
    const containerRect = container.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    
    // Adjust measurements based on container position relative to wrapper
    textRect.left = containerRect.left - wrapperRect.left;
    textRect.right = containerRect.right - wrapperRect.left;
    
    // Get first and last text element for vertical measurement
    const firstElement = textElements[0];
    const lastElement = textElements[textElements.length - 1];
    
    // Use text metrics to get precise top and bottom measurements
    const firstRect = firstElement.getBoundingClientRect();
    const lastRect = lastElement.getBoundingClientRect();
    
    // Get precise vertical measurements
    textRect.top = firstRect.top - wrapperRect.top;
    textRect.bottom = lastRect.bottom - wrapperRect.top;
  }
  
  // Calculate positions ensuring lines stay outside text boundaries
  const textWidth = textRect.right - textRect.left;
  const textCenter = textRect.left + textWidth / 2;
  
  // Set exact positions for the lines that are guaranteed to be outside the text
  const rightLineX = textRect.right + 20; // 20px to the right of text
  const bottomLineY = textRect.bottom + 20; // 20px below text
  
  // Add horizontal measurement line (well below the text)
  const bottomLine = document.createElement('div');
  bottomLine.className = 'measurement-line horizontal';
  bottomLine.style.position = 'absolute';
  bottomLine.style.top = `${bottomLineY}px`;
  bottomLine.style.left = `${textCenter}px`;
  bottomLine.style.width = `${textWidth}px`;
  bottomLine.style.height = '3px'; // Make lines even thicker for visibility
  bottomLine.style.backgroundColor = '#ffffff';
  bottomLine.style.transform = 'translateX(-50%)';
  bottomLine.style.zIndex = '998'; // High z-index
  measurementContainer.appendChild(bottomLine);
  
  // Add vertical measurement line that will be precisely positioned
  const rightLine = document.createElement('div');
  rightLine.className = 'measurement-line vertical';
  rightLine.style.position = 'absolute';
  rightLine.style.top = '0px'; // Will be adjusted with exact font ascent
  rightLine.style.left = `${rightLineX}px`;
  rightLine.style.width = '3px'; // Thick line for visibility
  rightLine.style.height = '1px'; // Will be adjusted to exact font height
  rightLine.style.backgroundColor = '#ffffff';
  rightLine.style.zIndex = '998'; // High z-index
  rightLine.id = 'vertical-measurement-line';
  // Important: add pointer events none to ensure it doesn't interfere with text
  rightLine.style.pointerEvents = 'none';
  measurementContainer.appendChild(rightLine);
  
  // Explicitly adjust vertical line after all text is rendered
  // Create a function to properly position the line after render
  function adjustVerticalLine() {
    if (textElements.length === 0) return;
    
    // Get the first and last text elements
    const firstElement = textElements[0];
    const lastElement = textElements[textElements.length - 1];
    
    // Find the parent wrapper elements for more accurate positioning
    const firstParent = firstElement.parentElement;
    const lastParent = lastElement.parentElement;
    
    // Get computed styles for font metrics
    const computedStyle = window.getComputedStyle(firstElement);
    const fontSize = parseFloat(computedStyle.fontSize);
    
    // Measure the exact content boundaries using the parent wrappers
    // This gives us the most accurate top and bottom points
    const firstTop = firstParent.offsetTop;
    const lastBottom = lastParent.offsetTop + lastParent.offsetHeight;
    
    // Calculate exact text height from top of first line to bottom of last line
    const textHeight = lastBottom - firstTop;
    
    console.log('Parent-based measurements:', firstTop, lastBottom, textHeight);
    
    // Position the vertical line exactly at the text boundaries, no shadows included
    rightLine.style.top = `${firstTop}px`;
    rightLine.style.height = `${textHeight}px`;
    
    // Update height label with new position and fixed 2-inch height, if it exists
    try {
      if (this.heightLabelContainer) {
        this.heightLabelContainer.style.top = `${firstTop + (textHeight / 2)}px`;
        
        // Count characters in the longest line
        let maxChars = 0;
        textElements.forEach(el => {
          maxChars = Math.max(maxChars, el.textContent.length);
        });
        
        // Calculate width: 2 inches * character count
        const displayWidth = (2 * maxChars).toFixed(1);
        this.widthLabelText.textContent = `${displayWidth}\u2033`;
        
        // Calculate aspect ratio for height
        const aspectRatio = rightLine.previousSibling.offsetWidth / textHeight;
        
        // Calculate height based on width and aspect ratio
        const displayHeight = (displayWidth / aspectRatio).toFixed(1);
        this.heightLabelText.textContent = `${displayHeight}\u2033`;
      }
    } catch (e) {
      console.log('Could not update dimension labels:', e);
    }
  }
  
  // Execute the adjustment immediately
  adjustVerticalLine();
  
  // Multiple timeouts with increasing delays to ensure we get the final measurements
  // Some browsers need additional time to calculate exact font metrics
  setTimeout(adjustVerticalLine, 10);
  setTimeout(adjustVerticalLine, 50);
  setTimeout(() => {
    adjustVerticalLine();
    
    // Force all measurement elements to have higher z-index
    const allMeasurementElements = measurementContainer.querySelectorAll('*');
    allMeasurementElements.forEach(el => {
      el.style.zIndex = '999';
    });
    
    console.log('Forced visibility of all measurement elements');
  }, 100);
  
  // Final alignment check after all other operations are complete
  setTimeout(adjustVerticalLine, 300);
  
  // Create a highly visible width label that will definitely show up
  const widthLabelContainer = document.createElement('div');
  widthLabelContainer.className = 'dimension-label-container width';
  widthLabelContainer.style.position = 'absolute';
  widthLabelContainer.style.top = `${bottomLineY + 15}px`; // 15px below the horizontal line
  widthLabelContainer.style.left = `${textCenter}px`;
  widthLabelContainer.style.transform = 'translateX(-50%)';
  widthLabelContainer.style.backgroundColor = '#000000';
  widthLabelContainer.style.border = '2px solid #ffffff';
  widthLabelContainer.style.padding = '4px 8px';
  widthLabelContainer.style.borderRadius = '4px';
  widthLabelContainer.style.zIndex = '999'; // Very high z-index
  
  // Create the text element inside the container using 2 inches per character
  const widthLabelText = document.createElement('span');
  
  // Count characters in the longest line
  let maxCharCount = 0;
  textElements.forEach(el => {
    maxCharCount = Math.max(maxCharCount, el.textContent.length);
  });
  
  // Calculate width: 2 inches * character count
  const displayWidth = (2 * maxCharCount).toFixed(1);
  
  widthLabelText.textContent = `${displayWidth}\u2033`;
  widthLabelText.style.color = '#ffffff';
  widthLabelText.style.fontSize = '16px';
  widthLabelText.style.fontWeight = 'bold';
  widthLabelText.style.fontFamily = 'Arial, sans-serif';
  widthLabelText.style.lineHeight = '1';
  widthLabelText.style.display = 'block';
  
  // Add the text to the container
  widthLabelContainer.appendChild(widthLabelText);
  measurementContainer.appendChild(widthLabelContainer);
  
  // Store reference for later updates
  this.widthLabelText = widthLabelText;
  
  // Create a highly visible height label that will definitely show up
  const heightLabelContainer = document.createElement('div');
  heightLabelContainer.className = 'dimension-label-container height';
  heightLabelContainer.style.position = 'absolute';
  heightLabelContainer.style.top = `${(textRect.top + textRect.bottom) / 2}px`; // Center of text height
  heightLabelContainer.style.left = `${rightLineX + 15}px`; // 15px to the right of the vertical line
  heightLabelContainer.style.transform = 'translateY(-50%)';
  heightLabelContainer.style.backgroundColor = '#000000';
  heightLabelContainer.style.border = '2px solid #ffffff';
  heightLabelContainer.style.padding = '4px 8px';
  heightLabelContainer.style.borderRadius = '4px';
  heightLabelContainer.style.zIndex = '999'; // Very high z-index
  
  // Create the text element inside the container - height based on width and aspect ratio
  const heightLabelText = document.createElement('span');
  
  // Calculate aspect ratio
  const aspectRatio = textWidth / (textRect.bottom - textRect.top);
  
  // Count characters in the longest line
  let maxChars = 0;
  textElements.forEach(el => {
    maxChars = Math.max(maxChars, el.textContent.length);
  });
  
  // Calculate width: 2 inches * character count
  const calcWidth = 2 * maxChars;
  
  // Calculate height based on width and aspect ratio
  const displayHeight = (calcWidth / aspectRatio).toFixed(1);
  
  heightLabelText.textContent = `${displayHeight}\u2033`;
  heightLabelText.style.color = '#ffffff';
  heightLabelText.style.fontSize = '16px';
  heightLabelText.style.fontWeight = 'bold';
  heightLabelText.style.fontFamily = 'Arial, sans-serif';
  heightLabelText.style.lineHeight = '1';
  heightLabelText.style.display = 'block';
  
  // Add the text to the container
  heightLabelContainer.appendChild(heightLabelText);
  measurementContainer.appendChild(heightLabelContainer);
  
  // Store references for later update by the vertical line adjustment function
  this.heightLabelContainer = heightLabelContainer;
  this.heightLabelText = heightLabelText;
}

/**
 * Calculates the price based on dimensions and updates all price displays.
 * 
 * @param {number} widthInches - Width of the neon text in inches
 * @param {number} heightInches - Height of the neon text in inches
 */
function calculateAndUpdatePrice(widthInches, heightInches) {
  // Calculate the area in square inches
  const area = widthInches * heightInches;
  
  // Apply basic price calculation
  let totalPrice = Math.max(BASE_PRICE, area * 2.5);
  totalPrice = Math.round(totalPrice); // Round to nearest dollar
  
  // Calculate discount price (20% off)
  const discountPrice = Math.round(totalPrice * 0.8);
  
  // Update the neonObject with prices
  neonObject.totalPrice = totalPrice;
  neonObject.discountPrice = discountPrice;
  
  // Update the price displays in the UI
  updatePriceDisplays(totalPrice, discountPrice);
  
  console.log('Price calculated:', totalPrice, 'discount price:', discountPrice);
}

// Listen for window resize
window.addEventListener("resize", debounce(function() {
  updateNeonText();
}, 250));

// TEXT RENDERING AND MEASUREMENT
// ==========================================================================

/**
 * Renders text with neon effect using HTML/CSS and calculates measurements.
 * This function creates HTML elements for the text and measurement guides.
 * 
 * @param {string} text - The text to render
 * @param {string} font - Font style string (e.g., "60px Avante")
 * @param {number} lineHeight - Line height in pixels
 * @param {string} containerId - ID of the container element
 * @param {string} HexColor - Hex color code for the neon effect
 * @param {boolean} showMeasurement - Whether to show measurement guides
 * @param {number} width - Width override (optional)
 * @param {number} height - Height override (optional)
 * @param {boolean} update - Whether this is an update to existing text
 * @return {Object} Object containing width and height in inches
 */
/**
 * Simplified text rendering function that generates neon text using HTML/CSS.
 */
function drawTextWithMeasurement(
  text,
  font,
  lineHeight,
  containerId,
  HexColor,
  showMeasurement,
  width,
  height,
  update
) {
  console.log('Drawing text:', text, 'in container:', containerId, 'with color:', HexColor);
  
  // Get the target container element
  const container = document.getElementById(containerId);
  if (!container) {
    console.error('Container not found:', containerId);
    return null;
  }
  
  console.log('Container found:', container);
  
  // Add a visible debugging style
  container.style.border = '2px solid red';
  container.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
  
  // Get parent element's dimensions
  const parentElement = container.parentElement;
  const parentWidth = parentElement.clientWidth;
  const parentHeight = parentElement.clientHeight;
  
  // Set container dimensions
  container.style.width = `${parentWidth * 0.8}px`;
  container.style.height = `${parentHeight * 0.8}px`;
  container.style.position = 'relative';
  container.style.overflow = 'hidden';
  
  const containerRect = container.getBoundingClientRect();
  
  // Clear existing content
  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }
  
  // Parse font size and family
  const fontSize = parseInt(font.match(/\d+px/)[0]);
  const fontFamily = font.replace(/\d+px\s*/, "");
  
  // Split text into lines
  const lines = text.split("\n");
  
  // Arrays to track positions and dimensions
  const maxX = [];
  const minX = [];
  const minY = [];
  const maxY = [];
  const textYPositions = [];
  
  // Create a wrapper div for all text elements with consistent styling
  const textWrapper = document.createElement('div');
  textWrapper.className = 'neon-text-wrapper';
  textWrapper.style.position = 'relative';
  textWrapper.style.width = '100%';
  textWrapper.style.height = '100%';
  textWrapper.style.display = 'flex';
  textWrapper.style.flexDirection = 'column';
  textWrapper.style.justifyContent = 'center';
  textWrapper.style.alignItems = 'center';
  textWrapper.style.overflow = 'hidden';
  container.appendChild(textWrapper);
  
  /**
   * Measures text dimensions using a temporary DOM element.
   * This provides accurate measurements for the given text with specific font properties.
   * 
   * @param {string} text - The text to measure
   * @param {number} fontSize - Font size in pixels
   * @param {string} fontFamily - Font family name
   * @return {Object} Metrics object containing width, height, ascent, and descent
   */
  /**
 * Measures text dimensions using a temporary DOM element.
 * This provides accurate measurements for the given text with specific font properties.
 * 
 * @param {string} text - The text to measure
 * @param {number} fontSize - Font size in pixels
 * @param {string} fontFamily - Font family name
 * @return {Object} Metrics object containing width, height, ascent, and descent
 */
function measureText(text, fontSize, fontFamily) {
  // Create a temporary, invisible element for measurement
  const testElement = document.createElement('div');
  testElement.style.position = 'absolute';
  testElement.style.visibility = 'hidden';
  testElement.style.fontSize = `${fontSize}px`;
  testElement.style.fontFamily = fontFamily;
  testElement.style.whiteSpace = 'nowrap';
  testElement.style.fontVariantLigatures = 'no-common-ligatures'; // Consistent with rendering
  testElement.textContent = text;
  document.body.appendChild(testElement);
  
  // Get the measurements
  const metrics = {
    width: testElement.offsetWidth,
    height: testElement.offsetHeight,
    // Estimate ascent and descent based on typical proportions
      // This could be improved with more precise font metrics if available
      ascent: testElement.offsetHeight * 0.7,  // ~70% for ascent is typical
      descent: testElement.offsetHeight * 0.3   // ~30% for descent is typical
    };
    
    // Clean up by removing the temporary element
    document.body.removeChild(testElement);
    return metrics;
  }
  
  // Position text lines
  let currentY = containerRect.height * 0.10; // Starting Y position
  const extraSpacing = 3; // Extra spacing between lines
  
  lines.forEach((line, index) => {
    // Measure line dimensions
    const metrics = measureText(line, fontSize, fontFamily);
    
    // Center text horizontally
    const xPos = (containerRect.width - metrics.width) / 2;
    const yPos = currentY + metrics.ascent;
    
    // Create HTML text element
    const textElement = document.createElement('div');
    textElement.className = 'neon-text-line mainText';
    textElement.textContent = line;
    textElement.style.position = 'absolute';
    textElement.style.left = `${xPos}px`;
    textElement.style.top = `${currentY}px`;
    textElement.style.fontFamily = fontFamily;
    textElement.style.fontSize = `${fontSize}px`;
    textElement.style.whiteSpace = 'nowrap';
    textElement.style.fontVariantLigatures = 'no-common-ligatures';
    textElement.style.zIndex = '10'; // Ensure text is above other elements
    console.log('Created text element:', line, 'at position:', xPos, currentY);
    textWrapper.appendChild(textElement);
    
    // Apply neon effect
    applyNeonEffect(textElement, HexColor);
    
    // Track positions for measurements
    minX.push(xPos);
    maxX.push(xPos + metrics.width);
    minY.push(currentY);
    maxY.push(currentY + metrics.height);
    textYPositions.push(yPos);
    
    // Update Y position for next line
    currentY += metrics.ascent + metrics.descent + extraSpacing;
  });
  
  // Auto-adjust font size if text exceeds container bounds
  while (
    (Math.max(...maxX) > containerRect.width * 0.9 ||
     Math.max(...maxY) > containerRect.height * 0.9) &&
    fontSize > 5
  ) {
    // Reduce font size
    fontSize--;
    
    // Clear text wrapper
    while (textWrapper.firstChild) {
      textWrapper.removeChild(textWrapper.firstChild);
    }
    
    // Reset measurement arrays
    maxX.length = 0;
    minX.length = 0;
    maxY.length = 0;
    minY.length = 0;
    textYPositions.length = 0;
    
    // Recalculate with new font size
    currentY = containerRect.height * 0.10;
    
    lines.forEach((line, index) => {
      const metrics = measureText(line, fontSize, fontFamily);
      const xPos = (containerRect.width - metrics.width) / 2;
      const yPos = currentY + metrics.ascent;
      
      const textElement = document.createElement('div');
      textElement.className = 'neon-text-line mainText';
      textElement.textContent = line;
      textElement.style.position = 'absolute';
      textElement.style.left = `${xPos}px`;
      textElement.style.top = `${currentY}px`;
      textElement.style.fontFamily = fontFamily;
      textElement.style.fontSize = `${fontSize}px`;
      textElement.style.whiteSpace = 'nowrap';
      textElement.style.fontVariantLigatures = 'no-common-ligatures';
      textWrapper.appendChild(textElement);
      
      applyNeonEffect(textElement, HexColor);
      
      minX.push(xPos);
      maxX.push(xPos + metrics.width);
      minY.push(currentY);
      maxY.push(currentY + metrics.height);
      textYPositions.push(yPos);
      
      currentY += metrics.ascent + metrics.descent + extraSpacing;
    });
  }
  
    /**
   * Applies a neon glow effect to a text element.
   * 
   * @param {HTMLElement} element - The DOM element to apply the effect to
   * @param {string} color - The hex color code for the neon glow
   */
  function applyNeonEffect(element, color) {
    console.log('Applying neon effect with color:', color);
    
    // Remove any existing color classes
    element.classList.forEach(className => {
      if (className.startsWith('neon-color-')) {
        element.classList.remove(className);
      }
    });
    
    // Add transition class for smooth effect changes
    element.classList.add("text-transition");
    
    // Set base text color to white to enhance glow effect
    element.style.color = "white";
    
    // Create the CSS shadow effect for the neon glow
    // Multiple layers with different blur radii create the realistic glow effect
    element.style.textShadow = 
      `${color} 0px 0px 2px, ` +
      `${color} 0px 0px 5px, ` +
      `${color} 0px 0px 10px, ` +
      `${color} 0px 0px 20px, ` +
      `${color} 0px 0px 30px, ` +
      `${color} 0px 0px 40px, ` +
      `${color} 0px 0px 55px, ` +
      `${color} 0px 0px 65px, ` +
      `${color} 0px 0px 75px`;
    
    // Add a color-specific class for potential CSS customization
    const colorClass = `neon-color-${color.replace('#', '')}`;
    element.classList.add(colorClass);
  }
  
  /**
   * Converts a hex color code to an RGB string.
   * 
   * @param {string} hex - The hex color code to convert
   * @return {string} A comma-separated RGB value string (e.g., "255, 0, 128")
   */
  function hexToRgb(hex) {
    // Remove # if present
    hex = hex.replace("#", "");
    
    // Parse the hex value to extract RGB components
    const intValue = parseInt(hex, 16);
    const r = (intValue >> 16) & 255;
    const g = (intValue >> 8) & 255;
    const b = intValue & 255;
    
    return `${r}, ${g}, ${b}`;
  }
  
  /**
   * Applies a normal (non-neon) text shadow effect to an element.
   * Used when neon effect is toggled off.
   * 
   * @param {HTMLElement} element - The DOM element to apply the effect to
   * @param {string} color - The hex color code for the text and shadow
   */
  function applyNormalEffect(element, color) {
    const rgbColor = hexToRgb(color);
    
    // Add transition class for smooth effect changes
    element.classList.add("text-transition");
    
    // Apply a subtle shadow effect for non-neon text
    element.style.textShadow = `
      rgba(${rgbColor},0.5) 0px 1px 1px,  /* Subtle close shadow */
      rgba(${rgbColor},0.5) 0px 2px 1px,  /* Medium close shadow */
      rgba(${rgbColor},0.6) 0px 4px 2px,  /* Medium distant shadow */
      rgba(0,0,0,0.6) 0px 5px 2px         /* Final drop shadow */
    `;
    
    // Set the text color to the specified color
    element.style.color = color;
  }
  
  // Add event listener for neon toggle
  document.getElementById("switch-rounded").addEventListener("change", (e) => {
    const textElements = document.querySelectorAll(`#${containerId} .mainText`);
    textElements.forEach((textElement) => {
      if (e.target.checked) {
        applyNeonEffect(textElement, HexColor);
      } else {
        applyNormalEffect(textElement, HexColor);
      }
    });
  });
  
  // Calculate the width and height in inches
  const maxXValue = Math.max(...maxX);
  const minXValue = Math.min(...minX);
  const totalWidthPixels = maxXValue - minXValue;

  const maxYValue = Math.max(...maxY);
  const minYValue = Math.min(...minY);
  const totalHeightPixels = maxYValue - minYValue;

  // Convert pixels to inches using a consistent pixels-per-inch ratio
  // Standard web resolution is 96 DPI (dots per inch)
  const pixelsPerInch = 96;
  
  // Apply a slight adjustment factor to account for neon tubing requirements
  const widthInches = totalWidthPixels / pixelsPerInch;
  const heightInches = totalHeightPixels / pixelsPerInch;
  
  // Return the calculated dimensions
  return {
    widthInches: widthInches,
    heightInches: heightInches
  };
}

/**
 * Updates all price displays in the UI with the given values.
 * 
 * @param {number} totalPrice - The total price before discount
 * @param {number} discountPrice - The price after discount
 */
function updatePriceDisplays(totalPrice, discountPrice) {
  // Update regular price displays
  const prevPriceElements = document.querySelectorAll(".prev-price");
  if (prevPriceElements && prevPriceElements.length > 0) {
    prevPriceElements.forEach((element) => {
      element.textContent = `$${totalPrice}`;
    });
  }

  // Update discounted price displays
  const discountPriceElements = document.querySelectorAll(".discount-price");
  if (discountPriceElements && discountPriceElements.length > 0) {
    discountPriceElements.forEach((element) => {
      element.textContent = `$${discountPrice}`;
    });
  }
}

// ==========================================================================
// NAVIGATION CONTROLS
// ==========================================================================
const boxes = document.querySelectorAll(".toggle");
const currentStep = document.getElementById("current-step");
const totalStep = document.getElementById("total-step");
totalStep.textContent = boxes.length;
const previousBtn = document.getElementById("previous");
const nextBtn = document.getElementById("next");
previousBtn.classList.add("disabled-btn");
let currentActive = 0;
const priceBox = document.querySelector(".price__box");

nextBtn.addEventListener("click", () => {
if (currentActive < boxes.length - 1) {
  currentActive++;
  currentStep.textContent = currentActive + 1;
  previousBtn.classList.remove("disabled-btn");
  boxes[currentActive - 1].classList.remove("activee");
  boxes[currentActive].classList.add("activee");
  if (currentActive === 7) {
    priceBox.classList.remove("cart-hide");
  }
} else {
  const panel = document.querySelector(".neon__ri");
  const neonArea = document.querySelector(".neon__area");
  neonArea.scrollIntoView({ behavior: "smooth", position: "start" });
  setTimeout(() => {
    panel.scrollTo({ top: 300, behavior: "smooth" });
  }, 500);
}
});

previousBtn.addEventListener("click", () => {
priceBox.classList.add("cart-hide");
currentActive--;
currentStep.textContent = currentActive + 1;
nextBtn.classList.remove("disabled-btn");
if (currentActive === 0) {
  previousBtn.classList.add("disabled-btn");
}
boxes[currentActive + 1].classList.remove("activee");
boxes[currentActive].classList.add("activee");
});

// ==========================================================================
// CHECKOUT FUNCTIONALITY
// ==========================================================================
// Select "Add to Cart" buttons for desktop and mobile.
const addToCartButtonDesktop = document.getElementById('add_to_cart_desktop');
const addToCartButtonMobile = document.getElementById('add_to_cart_mobile');

// Attach click listeners if buttons exist.
if (addToCartButtonDesktop) {
addToCartButtonDesktop.addEventListener('click', async (event) => {
  event.preventDefault();
  handleAddToCart();
});
}
if (addToCartButtonMobile) {
addToCartButtonMobile.addEventListener('click', async (event) => {
  event.preventDefault();
  handleAddToCart();
});
}

// handleAddToCart: Prepares checkout data without using canvas.
/**
 * Prepares the neon sign data for checkout by creating a structured data object
 * instead of relying on canvas/image capture.
 * 
 * This function completely eliminates canvas usage by storing text properties directly.
 * 
 * @return {Promise<void>} A promise that resolves when checkout preparation is complete
 */
async function handleAddToCart() {
  try {
    // Create a comprehensive structured representation of the neon text
    const neonTextData = {
      // Text content and styling
      text: neonObject.text,
      font: neonObject.fontFamily,
      fontSize: neonObject.fontSize,
      color: neonObject.color,
      
      // Physical dimensions
      widthInches: neonObject.widthInches,
      heightInches: neonObject.heightInches,
      
      // Pricing information
      totalPrice: neonObject.totalPrice,
      discountPrice: neonObject.discountPrice,
      discountApplied: neonObject.discountApplied,
      
      // Selected plan info
      planId: neonObject.plan.id || null,
      
      // Metadata
      timestamp: new Date().toISOString()
    };
    
    // Store the text data as a JSON string instead of an image URL
    neonObject.textData = JSON.stringify(neonTextData);
    
    // For backward compatibility, set imageURL to null
    neonObject.imageURL = null;
    
    // Proceed with checkout
    await createCheckout();
  } catch (error) {
    console.error('Error preparing checkout:', error);
  }
}

// createCheckout: Calls the backend to create a draft order and redirects to checkout.
async function createCheckout() {
try {
  const response = await fetch('https://api.texascustomneon.com/create-draft-order', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(neonObject),
  });
  const result = await response.json();
  window.location.href = result.checkout_url;
} catch (error) {
  console.error('Error creating checkout:', error);
}
}

// ==========================================================================
// FORM HANDLING
// ==========================================================================
// const form = document.getElementById('myForm');
// form.addEventListener('submit', async (event) => {
// event.preventDefault(); // Prevent default form submission
// const formData = new FormData(form);
// try {
//   const response = await fetch('https://api.texascustomneon.com/submit-form', {
//     method: 'POST',
//     body: formData
//   });
//   if (response.ok) {
//     const result = await response.json();
//     document.getElementById('thankYouMessage').style.display = 'block';
//   } else {
//     console.error('Error submitting form:', response.statusText);
//   }
// } catch (error) {
//   console.error('Error submitting form:', error);
// }
// });
document.addEventListener("DOMContentLoaded", function () {
  const form = document.getElementById("business-logo-form");
  const thankYouMessage = document.getElementById("thankYouMessage");
  const loadingSpinner = document.getElementById("loadingSpinner");

  if (!form) {
    console.error("Form element not found: #business-logo-form");
    return;
  }

  form.addEventListener("submit", async function (event) {
    event.preventDefault();

    // Hide previous messages
    thankYouMessage.style.display = "none";

    const formData = new FormData(form);
    const name = formData.get("name").trim();
    const email = formData.get("email").trim();
    const message = formData.get("message").trim();
    const file = formData.get("file");

    let isValid = true;

    // Validation
    if (!name) {
      alert("Please enter your name.");
      isValid = false;
    }
    if (!email || !validateEmail(email)) {
      alert("Please enter a valid email.");
      isValid = false;
    }
    if (!message) {
      alert("Please describe your request.");
      isValid = false;
    }

    if (!isValid) {
      return; // Stop submission if validation fails
    }

    // Show loading spinner
    loadingSpinner.style.display = "flex";

    try {
      // Submit the form data to backend
      const response = await fetch("https://api.texascustomneon.com/submit-form", {
        method: "POST",
        body: formData, // Automatically handles file upload
      });

      if (response.ok) {
        const result = await response.json();
        thankYouMessage.style.display = "block";
        form.reset(); // Reset form after success
      } else {
        console.error("Error submitting form:", response.statusText);
        alert("An error occurred. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An unexpected error occurred. Please try again.");
    } finally {
      loadingSpinner.style.display = "none";
    }
  });

  function validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }
});



// ---------------------------
// Discount Code Application
// ---------------------------
document.querySelectorAll('.apply-discount-button').forEach(button => {
button.addEventListener('click', async () => {
  const discountCodeInput = button.parentElement.querySelector('.discount-code-input');
  const discountCode = discountCodeInput.value.trim();
  const discountMessage = button.closest('.discount__input__wrapper').nextElementSibling;

  if (!discountCode) {
    discountMessage.textContent = 'Please enter a discount code.';
    return;
  }
  if (neonObject.discountApplied) {
    discountMessage.textContent = 'Discount code has already been applied.';
    return;
  }

  try {
    const response = await fetch('https://api.texascustomneon.com/apply-discount', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ discount_code: discountCode })
    });
    const data = await response.json();
    if (data.success) {
      if (data.discount_percentage) {
        neonObject.discountPrice *= (1 - data.discount_percentage / 100);
      } else if (data.discount_amount) {
        neonObject.discountPrice -= data.discount_amount;
      }
      neonObject.discountPrice = adjustToNinetyNine(neonObject.discountPrice);
      neonObject.discountApplied = true;
      document.querySelectorAll('.discount-price').forEach((element) => {
        element.textContent = `$${neonObject.discountPrice}`;
      });
      discountMessage.textContent = 'Discount applied successfully!';
      button.textContent = 'Remove';
      button.classList.remove('btn-primary');
      button.classList.add('btn-danger', 'remove-discount-button');
      button.classList.remove('apply-discount-button');

      button.addEventListener('click', () => {
        neonObject.discountApplied = false;
        neonObject.discountPrice = neonObject.originalDiscountPrice;
        document.querySelectorAll('.discount-price').forEach((element) => {
          element.textContent = `$${neonObject.discountPrice}`;
        });
        discountMessage.textContent = 'Discount removed.';
        button.textContent = 'Apply';
        button.classList.add('btn-primary');
        button.classList.remove('btn-danger', 'remove-discount-button');
        button.classList.add('apply-discount-button');
      });
    } else {
      discountMessage.textContent = data.message;
    }
  } catch (error) {
    discountMessage.textContent = 'Error applying discount.';
  }
});
});

// ---------------------------
// Initialization Calls
// ---------------------------
initFontPallate();
initFontSelection();
initEventListeners();




// Comprehensive scrolling solution that addresses neon__fx height issues

// Apply global scroll control that forces #slides to scroll first before the page
document.addEventListener('DOMContentLoaded', function() {
  console.log('Setting up comprehensive scrolling solution');

  // Fix for neon__fx 100vh height issue
  const neonFx = document.querySelector('.neon__fx');
  if (neonFx) {
    console.log('Found neon__fx element, fixing 100vh height issue');
    neonFx.style.height = 'auto';
    neonFx.style.minHeight = '100vh';
  }
  
  // Get the #slides element which is the main scrollable container
  const slidesContainer = document.getElementById('slides');
  
  if (!slidesContainer) {
    console.warn('#slides element not found! Cannot implement scroll prioritization');
    return;
  }
  
  // Configure #slides container for optimized scrolling
  slidesContainer.style.overflow = 'auto';
  slidesContainer.style.height = 'auto';
  slidesContainer.style.maxHeight = '70vh';
  slidesContainer.style.paddingBottom = '80px';
  
  // Force a reflow to apply styles immediately
  slidesContainer.offsetHeight;
  
  // Log scroll dimensions for debugging
  console.log('#slides config: height =', slidesContainer.scrollHeight, 'visible =', slidesContainer.clientHeight);
  
  // Track the last known scroll position to prevent jumps
  let lastScrollTop = window.scrollY || document.documentElement.scrollTop;
  
  // Set a flag to track scroll lock state
  let scrollLocked = false;
  
  // Calculate if slides container can be scrolled further down
  function canScrollDown() {
    if (!slidesContainer) return false;
    const maxScroll = slidesContainer.scrollHeight - slidesContainer.clientHeight;
    return slidesContainer.scrollTop < maxScroll;
  }
  
  // Calculate if slides container can be scrolled up
  function canScrollUp() {
    if (!slidesContainer) return false;
    return slidesContainer.scrollTop > 0;
  }
  
  // SUPER AGGRESSIVE: Replace native window.scrollTo to control scrolling
  const originalScrollTo = window.scrollTo;
  window.scrollTo = function(x, y) {
    // If we're in a controlled scroll, use the original function
    if (scrollLocked) {
      return originalScrollTo.apply(window, arguments);
    }
    
    // Otherwise, check if #slides needs to be scrolled first
    if (y > lastScrollTop && canScrollDown()) {
      // Trying to scroll down while #slides has more content
      slidesContainer.scrollTop += 50; // Scroll #slides first
      return; // Prevent window scroll
    }
    
    if (y < lastScrollTop && canScrollUp()) {
      // Trying to scroll up while #slides isn't at top
      slidesContainer.scrollTop -= 50; // Scroll #slides first
      return; // Prevent window scroll
    }
    
    // If #slides is already at limit, allow window scroll
    lastScrollTop = y;
    return originalScrollTo.apply(window, arguments);
  };
  
  // Primary scroll handler - captures ALL scroll events
  function captureAllScrolling(e) {
    // Skip if we're currently handling a scroll operation
    if (scrollLocked) return;
    
    // Lock scrolling to prevent recursive handling
    scrollLocked = true;
    
    try {
      // Normalize scroll delta to a reasonable amount
      const delta = e.deltaY || e.wheelDelta || -e.detail;
      const scrollAmount = Math.sign(delta) * Math.min(Math.abs(delta), 80);
      
      if (delta > 0) { // Scrolling DOWN
        if (canScrollDown()) {
          // Scroll the container instead of the page
          slidesContainer.scrollTop += scrollAmount;
          e.preventDefault();
          e.stopPropagation();
        }
      } else { // Scrolling UP
        if (canScrollUp()) {
          // Scroll the container up instead of the page
          slidesContainer.scrollTop += scrollAmount;
          e.preventDefault();
          e.stopPropagation();
        }
      }
    } finally {
      // Always unlock scrolling to prevent getting stuck
      setTimeout(() => { scrollLocked = false; }, 10);
    }
  }
  
  // Install multiple event listeners to catch ALL scrolling
  window.addEventListener('wheel', captureAllScrolling, { passive: false, capture: true });
  window.addEventListener('mousewheel', captureAllScrolling, { passive: false, capture: true });
  window.addEventListener('DOMMouseScroll', captureAllScrolling, { passive: false, capture: true });
  
  // Also monitor touch events for mobile scrolling
  let touchStartY = 0;
  
  window.addEventListener('touchstart', function(e) {
    touchStartY = e.touches[0].clientY;
  }, { passive: false, capture: true });
  
  window.addEventListener('touchmove', function(e) {
    if (scrollLocked) return;
    
    const touchY = e.touches[0].clientY;
    const touchDelta = touchStartY - touchY;
    
    if (touchDelta > 0) { // Scrolling DOWN
      if (canScrollDown()) {
        slidesContainer.scrollTop += Math.min(touchDelta, 50);
        e.preventDefault();
      }
    } else { // Scrolling UP
      if (canScrollUp()) {
        slidesContainer.scrollTop += Math.max(touchDelta, -50);
        e.preventDefault();
      }
    }
    
    touchStartY = touchY;
  }, { passive: false, capture: true });
  
  // Debug info
  console.log('Global scroll interception installed successfully');
});

// MAXIMUM OVERRIDE APPROACH using CSS HACKS AND JAVASCRIPT FORCED LAYOUT
window.addEventListener('load', function() {
  console.log('%c MAXIMUM STYLE OVERRIDE ACTIVATED', 'background: red; color: white; font-size: 20px; padding: 10px');
  
  // Insert a separate ultra-high-specificity stylesheet before any other method
  const ultraStyle = document.createElement('style');
  ultraStyle.id = 'ultra-override-styles';
  ultraStyle.innerHTML = `
    /* Super high specificity selector */
    html body .neon__fx .neon__lf,
    html body div.neon__fx div.neon__lf,
    .neon__fx > .neon__lf,
    body .neon__lf,
    div.neon__lf,
    .neon__lf {
      position: fixed !important;
      top: 120px !important;
      left: 0 !important;
      width: 62.7% !important;
      height: calc(100vh - 140px) !important;
      overflow: hidden !important;
      z-index: 9999 !important;
      margin: 0 !important;
      padding: 0 !important;
      transition: none !important;
    }
    
    html body .neon__fx .neon__ri,
    html body div.neon__fx div.neon__ri,
    body .neon__ri,
    div.neon__ri,
    .neon__ri {
      margin-left: 62.7% !important;
      width: 37.3% !important;
      height: auto !important;
      min-height: 100vh !important;
      display: flex !important;
      flex-direction: column !important;
    }
    
    #slides {
      max-height: 75vh !important;
      overflow-y: auto !important;
      padding-bottom: 100px !important;
    }
  `;
  
  // Insert at the BEGINNING of head to be overridden by our next style
  document.head.insertBefore(ultraStyle, document.head.firstChild);
  
  // Wait a bit to let other scripts run, then apply our aggressive override
  setTimeout(function() {
    console.log('Forcing fixed position with direct element manipulation');
    
    // Get references to our elements
    const neonLf = document.querySelector('.neon__lf');
    const neonRi = document.querySelector('.neon__ri');
    
    if (!neonLf || !neonRi) {
      console.error('Critical elements not found, cannot apply positioning');
      return;
    }
    
    console.log('Before override - neonLf current position:', window.getComputedStyle(neonLf).position);
    
    // HACK: Clone and replace the element to remove all event listeners and style baggage
    const neonLfClone = neonLf.cloneNode(true);
    neonLfClone.style.cssText = 'position: fixed !important; top: 120px !important; left: 0 !important; width: 62.7% !important; height: calc(100vh - 140px) !important; overflow: hidden !important; z-index: 9999 !important;';
    neonLf.parentNode.replaceChild(neonLfClone, neonLf);
    
    // Apply the same to the right side
    const neonRiClone = neonRi.cloneNode(true);
    neonRiClone.style.cssText = 'margin-left: 62.7% !important; width: 37.3% !important; height: auto !important; min-height: 100vh !important; display: flex !important; flex-direction: column !important;';
    neonRi.parentNode.replaceChild(neonRiClone, neonRi);
    
    console.log('After clone/replace - Is position fixed now?', window.getComputedStyle(neonLfClone).position);
    
    // Add a periodic check to ensure it stays fixed
    const intervalCheck = setInterval(function() {
      const currentNeonLf = document.querySelector('.neon__lf');
      if (currentNeonLf && window.getComputedStyle(currentNeonLf).position !== 'fixed') {
        console.log('Position was changed! Re-applying fixed positioning');
        currentNeonLf.style.cssText = 'position: fixed !important; top: 120px !important; left: 0 !important; width: 62.7% !important; height: calc(100vh - 140px) !important; overflow: hidden !important; z-index: 9999 !important;';
      }
    }, 500);
    
  }, 1000);  // Wait a full second to let everything else load first
}, { once: true });

// Duplicate the logic in DOMContentLoaded as a fallback
document.addEventListener('DOMContentLoaded', function() {
  console.log('DOMContentLoaded - Applying fixed neon display approach');
  
  // Create a style element with maximum priority (!important on everything)
  const styleEl = document.createElement('style');
  
  // Print all elements with the class neon__lf to help identify them
  console.log('Looking for all elements with class neon__lf');
  const allNeonLf = document.querySelectorAll('.neon__lf');
  console.log('Found ' + allNeonLf.length + ' elements with class neon__lf:');
  allNeonLf.forEach((el, index) => {
    console.log('Element #' + index + ':', el);
    console.log('- tagName:', el.tagName);
    console.log('- id:', el.id);
    console.log('- classList:', Array.from(el.classList));
    console.log('- parent:', el.parentElement);
    console.log('- computed position:', window.getComputedStyle(el).position);
    // Add a colored border to make it visible
    el.style.border = '5px solid red';
  });
  
  // Print all elements with the class neon__ri as well
  console.log('Looking for all elements with class neon__ri');
  const allNeonRi = document.querySelectorAll('.neon__ri');
  console.log('Found ' + allNeonRi.length + ' elements with class neon__ri:');
  allNeonRi.forEach((el, index) => {
    console.log('Element #' + index + ':', el);
    console.log('- parent:', el.parentElement);
    // Add a colored border to make it visible
    el.style.border = '5px solid blue';
  });
  
  // Define styles as backup to direct element manipulation
  styleEl.textContent = `
    /* Debug styles to visualize elements */
    .neon__lf { border: 5px solid red !important; }
    .neon__ri { border: 5px solid blue !important; }
    
    /* Backup styles for fixed left side with neon display */
    @media (min-width: 769px) {
      .neon__fx {
        height: auto !important;
        min-height: 100vh !important;
        display: flex !important;
        position: relative !important;
        align-items: flex-start !important;
      }
      
      .neon__lf {
        position: fixed !important;
        top: 120px !important;
        left: 0 !important;
        width: 62.7% !important;
        height: calc(100vh - 140px) !important;
        overflow: hidden !important;
      }
      
      .neon__ri {
        margin-left: 62.7% !important;
        width: 37.3% !important;
        height: auto !important;
        min-height: 100vh !important;
        display: flex !important;
        flex-direction: column !important;
      }
    }
    
    /* Primary scrolling container - #slides */
    #slides {
      overflow-y: auto !important;
      height: auto !important;
      max-height: 75vh !important;
      padding-bottom: 100px !important;
      scrollbar-width: thin !important;
      z-index: 5 !important;
      position: relative !important;
    }
    
    /* Make sure form fills available space */
    #custom-neon-form {
      height: 100% !important;
      display: flex !important;
      flex-direction: column !important;
    }
    
    /* Ensure smooth scrolling */
    html, body {
      scroll-behavior: smooth !important;
      overflow-x: hidden !important;
    }
    
    /* Custom scrollbar styling */
    #slides::-webkit-scrollbar {
      width: 10px !important;
    }
    
    #slides::-webkit-scrollbar-track {
      background: #151618 !important;
      border-radius: 5px !important;
    }
    
    #slides::-webkit-scrollbar-thumb {
      background: #888 !important;
      border-radius: 5px !important;
      border: 2px solid #151618 !important;
    }
    
    #slides::-webkit-scrollbar-thumb:hover {
      background: #555 !important;
    }
    
    /* Ensure proper display of content */
    .toggle {
      display: block !important;
    }
    
    /* Make price box sticky at bottom of form */
    .price__box {
      position: sticky !important;
      bottom: 0 !important;
      background: #151618 !important;
      z-index: 10 !important;
      padding-top: 10px !important;
      margin-top: 20px !important;
    }
    
    /* Add responsive adjustments for mobile */
    @media (max-width: 768px) {
      .neon__fx {
        flex-direction: column !important;
      }
      .neon__lf {
        position: relative !important;
        width: 100% !important;
        height: auto !important;
      }
      #slides {
        max-height: none !important;
      }
    }
  `;
  
  // Add the style element at the end of the head for highest priority
  document.head.appendChild(styleEl);
  
  // Apply direct styles to #slides for immediate effect
  const slidesContainer = document.getElementById('slides');
  if (slidesContainer) {
    Object.assign(slidesContainer.style, {
      overflowY: 'auto',
      height: 'auto',
      maxHeight: '70vh',
      paddingBottom: '80px',
      zIndex: '100'
    });
    
    // Force a reflow to ensure styles are applied
    slidesContainer.offsetHeight;
    
    console.log('#slides styled for scrolling, height:', slidesContainer.scrollHeight);
  } else {
    console.warn('#slides element not found for direct styling');
  }
  
  // Also adjust parent container
  const mainContainer = document.getElementById('main');
  if (mainContainer) {
    Object.assign(mainContainer.style, {
      overflow: 'visible',
      height: 'auto',
      position: 'relative'
    });
  }
});


//Fix scroll jump issue
(() => {
  const selectors = [
    ".choose__color___wrap .selector-item label",
    ".color__picker label",
  ];
  const fixSrollJump = () => {
    // Save the scroll position to restore it later
    const scrollPos = window.scrollY;
    
    // Apply to color selector labels
    document.querySelectorAll(selectors.join(",")).forEach((el) => {
      el.onclick = (e) => {
        // Skip if already handled by our custom code 
        if (e._handledByCustomCode) return;
        
        // Prevent default behavior
        e.preventDefault();
        
        // Get the associated input and check it
        const inputEl = document.getElementById(el.getAttribute("for"));
        if (inputEl) {
          inputEl.click();
          inputEl.checked = true;
          
          // Restore scroll position after the click
          setTimeout(() => {
            window.scrollTo(0, scrollPos);
          }, 10);
        }
      };
    });

    // Handle slides click actions separately
    const elArr = document.querySelectorAll(["#slides"].join(","));
    elArr.forEach((el) => {
      el.onclick = () => {
        document.querySelector("#sync1").scrollIntoView();
      };
    });
    
    // Add a global handler to prevent unwanted scrolling on color changes
    document.querySelectorAll('.selector-item_label').forEach(label => {
      label.addEventListener('mouseup', () => {
        // Restore scroll position after click is complete
        setTimeout(() => {
          window.scrollTo(0, scrollPos);
        }, 50);
      });
    });
  };
  if (document.readyState === "complete") {
    fixSrollJump();
  } else {
    window.addEventListener("DOMContentLoaded", fixSrollJump);
  }
})();
